import {
  addMonths,
  format as formatFns,
  formatISO,
  getDay,
  Interval,
  isBefore,
  isValid,
  parse,
  startOfDay,
} from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import clone from 'lodash/clone';
import intersectionBy from 'lodash/intersectionBy';
import pullAllBy from 'lodash/pullAllBy';
import unionBy from 'lodash/unionBy';

export const ISO_DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ssxxx";
export const ISO_DATE_FORMAT = 'yyyy-MM-dd';
export const ISO_TIME_FORMAT = 'HH:mm:ssxxx';
export const DISPLAY_DATE_FORMAT = 'dd-MM-yyyy';
export const DISPLAY_TIME_FORMAT = "HH:mm 'AEST'";
export const READABLE_DATE_FORMAT = 'd MMMM y';

export const today = startOfDay(new Date());

export const eighteenMonthsFromToday = addMonths(today, 18);

export const findByWeekday = (array: Date[], weekday: number): Date[] =>
  array.filter((d) => getDay(d) === weekday);

export const datesIntersection = (a: Date[], b: Date[]): Date[] =>
  intersectionBy(a, b, String);

export const includesDate = (dates: Date[], date: Date): boolean =>
  Boolean(datesIntersection(dates, [date]).length);

export const withoutDates = (a: Date[], b: Date[]): Date[] =>
  pullAllBy(clone(a), b, String);

export const unionDates = (a: Date[], b: Date[]): Date[] =>
  unionBy(a, b, String);

export const addTimeToDate = (date: Date, time: string): Date =>
  parse(time, 'HH:mm', date);

export const isIntervalValid = ({ start, end }: Interval) =>
  isValid(start) && isValid(end) && isBefore(start, end);

export const formatISODate = (date: Date) =>
  formatISO(date, {
    representation: 'date',
  });

export const formatISOTime = (date: Date) =>
  formatISO(date, {
    representation: 'time',
  });

export function formatDate({
  date,
  timeZone,
  dateFormat,
}: {
  date?: Date | null;
  timeZone?: string;
  dateFormat?: string;
}): string {
  if (!date) return '';

  return format(new Date(date), dateFormat ? dateFormat : 'd MMM yy', {
    timeZone: timeZone,
  });
}

export function formatYYMM(dateString?: string | null): string {
  if (!dateString || dateString.length !== 4) return '';

  const year = dateString.substring(0, 2);
  const month = dateString.substring(2, 4);

  const date = parse(`${month}${year}`, 'MMyy', new Date());

  return formatFns(date, 'MM yyyy');
}

export const formatUtcDate = ({
  date,
  dateFormat,
}: {
  date: string;
  dateFormat: string;
}): string | undefined => {
  if (!date || !dateFormat) return undefined;

  const utcDate = utcToZonedTime(date, 'UTC');
  return formatFns(utcDate, dateFormat);
};

const AUS_FINANCIAL_YEAR_START_MONTH = 6; // July (0-indexed)

export const ausFinancialYearStart = (date = new Date()): Date => {
  const year =
    date.getMonth() + 1 > AUS_FINANCIAL_YEAR_START_MONTH
      ? date.getFullYear()
      : date.getFullYear() - 1;

  return new Date(Date.UTC(year, AUS_FINANCIAL_YEAR_START_MONTH, 1));
};

export const ausFinancialYearEnd = (date = new Date()): Date => {
  const year =
    date.getMonth() < AUS_FINANCIAL_YEAR_START_MONTH
      ? date.getFullYear()
      : date.getFullYear() + 1;

  return new Date(Date.UTC(year, AUS_FINANCIAL_YEAR_START_MONTH - 1, 30));
};
